/** @jsx jsx */
import React from "react";

import { jsx } from "@emotion/core";

import { Typography, Upload, Icon, Tag, Col, Row } from "antd";

import { Formik, Form, FormikHelpers } from "formik";

//Hooks
import useUploadBill from "../../../hooks/bill/useBill";

import { UploadBillPayload } from "../../../services/billService/BillService.type";

//Components
import { FormInput, FormInputNumber } from "../../../components/formHelpers";
import CustomButton from "../../../components/button";

import { UploadBillStyles } from "./UploadBillStyles";
import {
  uploadBillContents as CONSTANTS,
  challanFileType,
  uploadBillValidationSchema
} from "./UploadBillConstants";
import useEligibilityMetaData from "../../../hooks/auth/useEligibilityMetaData";
import useRedirect from "../../../helperHooks/useRedirect";
import { formatDate } from "../../../helper/Format";
import { validateFile } from "../../../helper/FileValidator";
import { DEFAULT_FILE_CONFIG } from "../../../Constants";
const { Text } = Typography;

const initialBillState: UploadBillPayload = {
  gstOutstandingAmount: null,
  challanId: "",
  challanAmount: null,
  file: null,
  challanType: "",
  processing_fees: 0
};

const UploadBill: React.FC = () => {
  const {
    uploadBill,
    billState: { loading }
  } = useUploadBill();
  const {
    eligibilityMetaData: { eligibilityAmount, amountUtilized, expiryDate }
  } = useEligibilityMetaData();
  const redirect = useRedirect();

  const remainingAmount = (eligibilityAmount || 0) - (amountUtilized || 0);

  const validationSchema = uploadBillValidationSchema(remainingAmount);

  const onSubmit = (
    values: UploadBillPayload,
    formikHelpers: FormikHelpers<UploadBillPayload>
  ) => {
    const { file } = values;
    if (file) {
      //All the bills are of type GST for this phase
      uploadBill({
        data: { ...values, challanType: "GST" },
        redirect: () => redirect(routes => routes.bills.new)
      });
    } else {
      formikHelpers.setFieldError(
        "file",
        CONSTANTS.feedbacks.requiredChallanFile
      );
    }
  };
  return (
    <div css={UploadBillStyles}>
      <Typography className="title mb-3">{CONSTANTS.titles.heading}</Typography>
      <Text>
        {CONSTANTS.titles.EligibilityText(
          remainingAmount,
          expiryDate ? formatDate(expiryDate) : "-"
        )}
      </Text>
      <Formik
        initialValues={initialBillState}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange
        validateOnMount
      >
        {({ setFieldValue, values, setFieldError, errors }) => (
          <Form>
            <Row>
              <Col span={8}>
                <FormInputNumber
                  name="gstOutstandingAmount"
                  className="mt-4 mb-3"
                  placeholder={CONSTANTS.placeholders.gstOutAmt}
                  label={CONSTANTS.labels.gstOutAmt}
                  mandatory
                />
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <FormInput
                  name="challanId"
                  className="mb-3"
                  placeholder={CONSTANTS.placeholders.challanId}
                  label={CONSTANTS.labels.challanId}
                  mandatory
                />
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <FormInputNumber
                  name="challanAmount"
                  className="mb-3"
                  placeholder={CONSTANTS.placeholders.challanAmount}
                  label={CONSTANTS.labels.challanAmount}
                  mandatory
                />
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <FormInputNumber
                  name="processing_fees"
                  className="mb-3"
                  placeholder={CONSTANTS.placeholders.processing_fees}
                  label={CONSTANTS.labels.processing_fees}
                  mandatory
                />
              </Col>
            </Row>
            <Upload
              name="file"
              className="mb-3"
              accept={challanFileType}
              beforeUpload={e => {
                const fileInvalid = validateFile(e, DEFAULT_FILE_CONFIG);
                if (fileInvalid) {
                  setFieldError("file", CONSTANTS.feedbacks.fileFormat);
                } else {
                  setFieldValue("file", e);
                }
                return false;
              }}
              showUploadList={false}
            >
              {CONSTANTS.buttons.challanFile}
              <Icon type="upload" className="upload-icon ml-5" />
            </Upload>
            <Row>
              <Col span={8}>
                {errors.file && (
                  //Not using ErrorMessage because custom error doesn't get rendered
                  <Typography className="error">{errors.file}</Typography>
                )}
              </Col>
            </Row>
            <Row gutter={8}>
              {values.file && (
                <Col span={8} className="file-list-container">
                  <Col span={10}>
                    <Tag
                      key={values.file.name}
                      closable
                      className="mt-3 d-flex ant-tag-ellipse ant-tag-grey"
                      onClose={() => setFieldValue("file", null)}
                    >
                      <Text className="ant-tag-name-ellipsis">
                        {values.file.name}
                      </Text>
                    </Tag>
                  </Col>
                </Col>
              )}
            </Row>
            <CustomButton
              htmlType="submit"
              className="mt-4"
              type="primary"
              loading={loading}
            >
              {CONSTANTS.buttons.submit}
            </CustomButton>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UploadBill;
